import { Container, Graphics, Text, TextStyle, Application } from 'pixi.js';
import { Upgrade } from './UpgradeSystem';

export default class UpgradeUI {
    private container: Container;
    private app: Application;
    private onSelectCallback: (upgrade: Upgrade) => void;

    constructor(app: Application, onSelectCallback: (upgrade: Upgrade) => void) {
        this.app = app;
        this.container = new Container();
        this.onSelectCallback = onSelectCallback;
        this.app.stage.addChild(this.container);
        this.container.visible = false;
    }

    public show(upgrades: Upgrade[]): void {
        this.container.removeChildren();
        this.container.visible = true;

        // Background overlay
        const overlay = new Graphics();
        overlay.rect(0, 0, this.app.screen.width, this.app.screen.height);
        overlay.fill({ color: 0x000000, alpha: 0.7 });
        overlay.eventMode = 'static'; // Block clicks
        this.container.addChild(overlay);

        // Title
        const titleStyle = new TextStyle({
            fontFamily: 'Arial',
            fontSize: 48,
            fill: '#FFD700',
            fontWeight: 'bold',
            stroke: { color: '#000000', width: 6 },
            dropShadow: {
                alpha: 0.5,
                angle: Math.PI / 6,
                blur: 4,
                color: '#000000',
                distance: 6,
            },
        });
        const title = new Text({ text: 'LEVEL UP!', style: titleStyle });
        title.anchor.set(0.5);
        title.x = this.app.screen.width / 2;
        title.y = 100;
        this.container.addChild(title);

        // Cards
        const cardWidth = 250;
        const cardHeight = 350;
        const gap = 50;
        const totalWidth = (upgrades.length * cardWidth) + ((upgrades.length - 1) * gap);
        const startX = (this.app.screen.width - totalWidth) / 2 + cardWidth / 2;

        upgrades.forEach((upgrade, index) => {
            const card = this.createCard(upgrade, cardWidth, cardHeight);
            card.x = startX + index * (cardWidth + gap);
            card.y = this.app.screen.height / 2;
            this.container.addChild(card);
        });
    }

    private createCard(upgrade: Upgrade, width: number, height: number): Container {
        const card = new Container();
        
        // Card Background
        const bg = new Graphics();
        bg.roundRect(-width/2, -height/2, width, height, 20);
        bg.fill({ color: 0xFFFFFF });
        bg.stroke({ width: 4, color: upgrade.color });
        
        // Hover effect
        card.eventMode = 'static';
        card.cursor = 'pointer';
        card.on('pointerover', () => {
            card.scale.set(1.1);
            bg.stroke({ width: 8, color: upgrade.color });
        });
        card.on('pointerout', () => {
            card.scale.set(1.0);
            bg.stroke({ width: 4, color: upgrade.color });
        });
        card.on('pointerdown', () => {
            this.onUpgradeSelected(upgrade);
        });

        card.addChild(bg);

        // Icon
        const iconStyle = new TextStyle({
            fontSize: 80,
        });
        const icon = new Text({ text: upgrade.icon, style: iconStyle });
        icon.anchor.set(0.5);
        icon.y = -80;
        card.addChild(icon);

        // Name
        const nameStyle = new TextStyle({
            fontFamily: 'Arial',
            fontSize: 24,
            fontWeight: 'bold',
            fill: 0x333333,
            wordWrap: true,
            wordWrapWidth: width - 40,
            align: 'center'
        });
        const name = new Text({ text: upgrade.name, style: nameStyle });
        name.anchor.set(0.5, 0);
        name.y = -20;
        card.addChild(name);

        // Description
        const descStyle = new TextStyle({
            fontFamily: 'Arial',
            fontSize: 18,
            fill: 0x666666,
            wordWrap: true,
            wordWrapWidth: width - 40,
            align: 'center'
        });
        const desc = new Text({ text: upgrade.description, style: descStyle });
        desc.anchor.set(0.5, 0);
        desc.y = 40;
        card.addChild(desc);

        return card;
    }

    private onUpgradeSelected(upgrade: Upgrade): void {
        this.container.visible = false;
        this.onSelectCallback(upgrade);
    }

    public update(): void {
        // potentially animate background or cards
    }
}

